import React, { useState, useEffect } from "react";
import axiosClient from "../../api/axiosClient";
import { X, CheckCircle, Loader2, Search, Copy, Zap, Info, AlertTriangle, Mail, Eye } from "lucide-react";
import WhatsAppPreviewModal from "./WhatsAppPreviewModal";
import AccesosEmailPreviewModal from "./AccesosEmailPreviewModal";
import { jwtDecode } from "jwt-decode";
import { SECTOR_LABEL_MAP } from "../../utils/sectorLabels";
import { ETIQUETA_PROFESOR } from "./sectorProfesor";
import { findPlantillaByName } from "../../utils/plantillas";
import {
  isValidEmail,
  buildAccesosEmailPayload,
  accesosEmailErrorMessage,
} from "../../utils/accesosEmail";

function generateUsername(nombre) {
  const base = (nombre || "cliente")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/(^_|_$)/g, "")
    .slice(0, 26);
  const suffix = Math.floor(10 + Math.random() * 90);
  return `${base || "cliente"}_${suffix}`;
}

// Usuario por defecto = n\u00famero de tel\u00e9fono nacional del lead (igual que la
// contrase\u00f1a). Los comerciales se quejaban de usuarios largos tipo
// "mr_car_wash_xativa_55". Solo lo usamos si tenemos un n\u00famero plausible
// (>= 6 d\u00edgitos); si no, caemos al generador basado en el nombre.
function phoneUsername(phoneLocal) {
  const digits = (phoneLocal || "").replace(/\D+/g, "");
  return digits.length >= 6 ? digits : null;
}

function CredentialRow({ label, value, color }) {
  return (
    <div>
      <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-1">
        {label}
      </label>
      <div className="flex items-center justify-between gap-2 p-3 bg-white border border-gray-200 rounded-xl">
        <code className={`text-sm font-bold text-${color}-600 break-all`}>{value}</code>
        <button
          type="button"
          onClick={() => navigator.clipboard?.writeText(value)}
          className="text-gray-400 hover:text-gray-600 p-1 flex-shrink-0"
          title="Copiar"
        >
          <Copy size={15} />
        </button>
      </div>
    </div>
  );
}

export default function SectorPickerModal({
  open,
  onClose,
  leadName,
  leadPhone,
  leadPhoneLocal,
  leadEmail,
  presetSector,
  // Plan Profesor (26/10/2026): "profesor" si el lead es «Profesor de autoescuela» (sectorProfesor.js). La cuenta
  // se crea como el alta web `signup?plan=profesor` (v_create_client → planProfesorAlta.js), nunca como autoescuela.
  presetPlan = null,
  contactId,
  onSuccess,
}) {
  // Si el lead ya tiene sector asignado, omitimos la selección y vamos
  // directamente al envío (o al paso de layout para lavadero).
  const normalizedPreset = (presetSector || "").toLowerCase();
  const presetIsValid = !!normalizedPreset;
  const planProfesor = presetIsValid && normalizedPreset === "autoescuela" && presetPlan === "profesor";
  const [sectors, setSectors] = useState([]);
  const [sectorsLoading, setSectorsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedSector, setSelectedSector] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // { username, password }
  // Lavadero: layout fijado a by_service (no se pregunta al comercial; el negocio
  // lo ajusta luego en el CRM). Mantenemos `step` por compatibilidad (siempre 1).
  const [step, setStep] = useState(1);
  const [lavaderoLayout, setLavaderoLayout] = useState("by_service"); // forzado
  const [sendByWhatsApp, setSendByWhatsApp] = useState(false);
  const [sendByEmail, setSendByEmail] = useState(false);
  // Email al que mandar las credenciales: el de la ficha, o el que teclee el
  // vendedor aquí mismo si el lead no lo tiene (se persiste en el lead al enviar).
  const [emailValue, setEmailValue] = useState("");
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [waPreviewOpen, setWaPreviewOpen] = useState(false);

  // Reset and load sectors on open
  useEffect(() => {
    if (!open) return;
    setStatus("idle");
    setError(null);
    setResult(null);
    setSearch("");
    setSelectedSector(presetIsValid ? normalizedPreset : "");
    setStep(1);
    setLavaderoLayout("by_service");
    setSendByWhatsApp(false);
    setSendByEmail(false);
    setEmailValue(leadEmail || "");
    setEmailPreviewOpen(false);
    // Si ya conocemos el sector, no necesitamos cargar el listado de sectores.
    if (presetIsValid) {
      setSectors([normalizedPreset]);
      setSectorsLoading(false);
      return;
    }
    setSectorsLoading(true);
    axiosClient
      .get("/admin/sector-config")
      .then((res) => {
        const list = Array.isArray(res?.configs)
          ? res.configs.map((c) => c.sector).filter(Boolean)
          : [];
        // "Marketing genérico" no vive en c_sector_config (la app de marketing es
        // pay-per-use, sin plantillas/servicios por sector) -> lo añadimos a mano
        // para poder crear accesos de prueba de la app de marketing.
        if (!list.map((s) => s.toLowerCase()).includes("marketing")) {
          list.push("marketing");
        }
        setSectors(list);
        if (list.length > 0) setSelectedSector(list[0]);
      })
      .catch(() => setSectors([]))
      .finally(() => setSectorsLoading(false));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredSectors = sectors.filter((s) => {
    const label = SECTOR_LABEL_MAP[s.toLowerCase()] || s;
    return label.toLowerCase().includes(search.toLowerCase());
  });

  const handleCreate = async () => {
    if (!selectedSector) return;
    // El email se valida ANTES de crear nada: crear la cuenta y fallar después
    // dejaría al vendedor con una empresa creada y un error confuso.
    const emailTrimmed = (emailValue || "").trim();
    if (sendByEmail && !isValidEmail(emailTrimmed)) {
      setError("Escribe un email válido para enviarle las credenciales.");
      setStatus("error");
      return;
    }
    setStatus("loading");
    setError(null);

    let vendorId = "";
    try {
      const tok = localStorage.getItem("token");
      if (tok) vendorId = jwtDecode(tok).user_id || "";
    } catch {}

    const tryCreate = (username) =>
      axiosClient.post("/v/create-client", {
        nombre: leadName,
        telefono: leadPhone,
        sector: selectedSector,
        admin_username: username,
        language: "es",
        use_sector_defaults: true,
        sales_vendor_id: vendorId,
        // Lead de origen: v_create_client siembra el catálogo/servicios que el
        // comercial personalizó en el simulador para este contacto.
        ...(contactId ? { contact_id: contactId } : {}),
        // Número nacional (sin prefijo) para usar como contraseña inicial.
        ...(leadPhoneLocal ? { password_seed: leadPhoneLocal } : {}),
        ...(selectedSector === "lavadero" && lavaderoLayout
          ? { service_layout: lavaderoLayout }
          : {}),
        ...(planProfesor && selectedSector === "autoescuela" ? { autoescuela_plan: "profesor" } : {}),
      });

    try {
      // Por defecto el usuario es el teléfono del lead (más corto y fácil de
      // dictar). Fallback al generador por nombre si no hay número válido.
      let username = phoneUsername(leadPhoneLocal) || generateUsername(leadName);
      let resp;

      try {
        resp = await tryCreate(username);
      } catch (err) {
        if (err?.response?.data?.error === "nombre_de_usuario_en_uso") {
          // Si el teléfono ya está en uso como usuario, reintentamos con un
          // nombre + sufijo aleatorio (siempre único).
          username = generateUsername(leadName);
          resp = await tryCreate(username);
        } else {
          throw err;
        }
      }

      const createdUsername = resp?.admin?.username || username;
      const createdPassword = resp?.admin?.password;
      const createdLoginCode = resp?.login_code || null;
      const createdCompanyId = resp?.company_id;

      if (!createdUsername || !createdPassword) {
        throw new Error("La cuenta se creó pero no se recibieron las credenciales.");
      }

      // El email va PRIMERO y en su PROPIO try/catch: si el envío por WhatsApp
      // revienta, el catch externo se llevaría el flujo entero y el email que
      // el vendedor pidió no llegaría ni a intentarse.
      let emailSent = false;
      let emailError = null;
      if (sendByEmail) {
        try {
          await axiosClient.post(
            "/ventas/accesos-email",
            buildAccesosEmailPayload({
              companyId: createdCompanyId,
              email: emailTrimmed,
              contactId,
              nombre: leadName,
            }),
          );
          emailSent = true;
          // Si el vendedor lo tecleó aquí, que quede en la ficha del lead.
          if (contactId && emailTrimmed !== (leadEmail || "").trim()) {
            axiosClient
              .post("/ventas/bulk-contacts", { contact_id: contactId, email: emailTrimmed })
              .catch((err) => console.warn("bulk-contacts persist email failed:", err));
          }
        } catch (err) {
          emailError = accesosEmailErrorMessage(err);
          console.warn("accesos-email failed:", emailError);
        }
      }

      if (sendByWhatsApp) {
        // Fetch templates to find acceso_plataforma
        const plantillasRes = await axiosClient.get("/plantillas");
        const tpl = findPlantillaByName(plantillasRes, "acceso_plataforma");

        // Send credentials via WhatsApp template
        // {{1}} = "username\nContraseña: password", {{2}} = app URL
        await axiosClient.post("/send-template", {
          phone: leadPhone,
          template_id: tpl?.template_id || tpl?.id || "acceso_plataforma",
          templateData: tpl || undefined,
          params: {
            "1": `${createdUsername}  Contraseña: ${createdPassword}`,
            "2": "app.conextcrm.es",
          },
        });
      }

      setResult({
        username: createdUsername,
        password: createdPassword,
        loginCode: createdLoginCode,
        sentByWhatsApp: sendByWhatsApp,
        sentByEmail: emailSent,
        emailError,
        email: emailSent ? emailTrimmed : null,
      });
      setStatus("success");
      onSuccess?.({
        username: createdUsername,
        password: createdPassword,
        login_code: createdLoginCode,
        phone: leadPhone,
        company_id: createdCompanyId,
        sentByWhatsApp: sendByWhatsApp,
        sentByEmail: emailSent,
        email: emailSent ? emailTrimmed : null,
      });
    } catch (err) {
      const specificError = err?.response?.data?.error;
      const displayMsg =
        specificError === "nombre_de_usuario_en_uso"
          ? "El nombre de usuario generado ya existe. Inténtalo de nuevo."
          : err?.response?.data?.message || err.message || "Error desconocido";
      setError(displayMsg);
      setStatus("error");
    }
  };

  if (!open) return null;

  return (
    <>
    <div className="fixed inset-0 z-[200] flex items-start sm:items-center justify-center p-3 sm:p-4 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={status !== "loading" ? onClose : undefined}
      />

      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-[95vw] sm:max-w-xl p-6 sm:p-8 z-10 my-auto max-h-[92vh] overflow-y-auto">
        {status === "success" && result ? (
          /* ── Success state ── */
          <div className="text-center space-y-6">
            <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle size={32} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                {result.sentByWhatsApp && result.sentByEmail
                  ? "¡Accesos enviados por WhatsApp y email!"
                  : result.sentByWhatsApp
                    ? "¡Accesos enviados por WhatsApp!"
                    : result.sentByEmail
                      ? "¡Accesos enviados por email!"
                      : "¡Cuenta creada!"}
              </h3>
              {result.emailError && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3 text-left">
                  La cuenta se creó, pero el email no salió: {result.emailError}. Puedes reenviarlo
                  desde el botón de accesos de la ficha.
                </p>
              )}
              <p className="text-sm text-gray-500 mt-1">
                {result.sentByWhatsApp ? (
                  <span className="inline-flex items-center justify-center gap-1.5">
                    <CheckCircle size={14} className="text-green-600 flex-shrink-0" />
                    Enviados por WhatsApp al número <span className="font-semibold">{leadPhone}</span>
                  </span>
                ) : result.sentByEmail ? (
                  <span className="inline-flex items-center justify-center gap-1.5">
                    <CheckCircle size={14} className="text-green-600 flex-shrink-0" />
                    Enviados por email a <span className="font-semibold">{result.email}</span>
                  </span>
                ) : (
                  <>Copia y comparte las credenciales manualmente.</>
                )}
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-6 text-left space-y-4 border border-gray-100">
              {/* Credenciales en 2 columnas para aprovechar el ancho y reducir scroll */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <CredentialRow label="Web de acceso" value="app.conextcrm.es" color="violet" />
                <CredentialRow label="Usuario" value={result.username} color="blue" />
                <CredentialRow label="Contraseña" value={result.password} color="rose" />
                {result.loginCode && (
                  <CredentialRow
                    label="Código rápido (usuario y contraseña)"
                    value={result.loginCode}
                    color="emerald"
                  />
                )}
              </div>
              {result.loginCode && (
                <p className="text-xs text-emerald-700 bg-emerald-50 rounded-xl p-3 border border-emerald-100 leading-relaxed flex items-start gap-2">
                  <Zap size={14} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>
                    Para entrar al instante: pon <strong>{result.loginCode}</strong> en
                    usuario y <strong>{result.loginCode}</strong> en contraseña.
                  </span>
                </p>
              )}
              <p className="text-xs text-red-700 bg-red-50 rounded-xl p-3 border border-red-100 leading-relaxed flex items-start gap-2">
                <Info size={14} className="text-red-600 flex-shrink-0 mt-0.5" />
                <span>El cliente deberá cambiar la contraseña en su primer acceso.</span>
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="w-full px-6 py-3 rounded-2xl bg-gradient-to-r from-red-600 to-violet-600 text-white font-bold hover:from-red-700 hover:to-violet-700 transition-all"
            >
              Cerrar
            </button>
          </div>
        ) : (
          /* ── Idle / loading / error state ── */
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  {sendByWhatsApp ? "Enviar accesos a la plataforma" : "Crear accesos a la plataforma"}
                </h3>
                <p className="text-sm text-gray-500 mt-0.5 truncate max-w-full sm:max-w-[280px]">
                  {leadName}
                </p>
              </div>
              {status !== "loading" && (
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 p-1 ml-2 flex-shrink-0"
                >
                  <X size={20} />
                </button>
              )}
            </div>

            {/* Error message */}
            {status === "error" && error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-xl flex items-start gap-2">
                <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {/* Sector selector — se omite si ya tenemos sector preestablecido */}
            {step === 1 && presetIsValid && (
              <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">
                  Sector
                </div>
                <div className="text-sm font-semibold text-gray-800">
                  {planProfesor ? ETIQUETA_PROFESOR : SECTOR_LABEL_MAP[normalizedPreset] || normalizedPreset}
                </div>
                {planProfesor && (
                  <div className="text-[11px] text-violet-700 font-semibold mt-0.5">
                    Cuenta del Plan Profesor (15 €/mes): una sola agenda, la suya, y sin Cobros, Flota, Cursos ni Fichajes.
                  </div>
                )}
                <div className="text-[11px] text-gray-500 mt-0.5">
                  Tomado del lead. Para cambiarlo edita el campo "Sector" del lead.
                </div>
              </div>
            )}
            {step === 1 && !presetIsValid && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Sector
              </label>
              <>
              {/* Search input */}
              <div className="relative mb-2">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar sector..."
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-300 bg-white"
                  disabled={status === "loading"}
                />
              </div>
              {/* Sector list */}
              <div role="listbox" aria-label="Sector" className="max-h-44 overflow-y-auto rounded-2xl border border-gray-100 divide-y divide-gray-50">
                {sectorsLoading && (
                  <div className="flex items-center justify-center gap-2 py-6 text-gray-400 text-sm">
                    <Loader2 size={16} className="animate-spin" />
                    Cargando sectores…
                  </div>
                )}
                {!sectorsLoading && filteredSectors.map((s) => {
                  const label =
                    SECTOR_LABEL_MAP[s.toLowerCase()] ||
                    s.charAt(0).toUpperCase() + s.slice(1);
                  return (
                    <button
                      key={s}
                      type="button"
                      role="option"
                      aria-selected={selectedSector === s}
                      onClick={() => setSelectedSector(s)}
                      disabled={status === "loading"}
                      className={`w-full text-left px-4 py-3 text-sm transition-colors disabled:pointer-events-none ${
                        selectedSector === s
                          ? "bg-red-50 text-red-700 font-semibold"
                          : "hover:bg-gray-50 text-gray-700"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
                {!sectorsLoading && filteredSectors.length === 0 && (
                  <p className="text-center text-gray-400 text-sm py-4">
                    Sin resultados
                  </p>
                )}
              </div>
              </>
            </div>
            )}

            {/* WhatsApp send toggle */}
            {step === 1 && (
              <label className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors">
                <input
                  type="checkbox"
                  checked={sendByWhatsApp}
                  onChange={(e) => setSendByWhatsApp(e.target.checked)}
                  disabled={status === "loading"}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                    Enviar credenciales por WhatsApp
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setWaPreviewOpen(true);
                      }}
                      className="ml-auto p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors"
                      title="Vista previa del mensaje de WhatsApp"
                    >
                      <Eye size={15} />
                    </button>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    Si lo dejas desactivado, solo se mostrarán en pantalla para copiarlas.
                  </div>
                </div>
              </label>
            )}

            {/* Email send toggle */}
            {step === 1 && (
              <div className="rounded-xl border border-gray-200 bg-gray-50">
                <label className="flex items-start gap-3 p-3 cursor-pointer hover:bg-gray-100 transition-colors rounded-xl">
                  <input
                    type="checkbox"
                    checked={sendByEmail}
                    onChange={(e) => setSendByEmail(e.target.checked)}
                    disabled={status === "loading"}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                      <Mail size={14} className="text-gray-500" />
                      Enviar credenciales por email
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setEmailPreviewOpen(true);
                        }}
                        className="ml-auto p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors"
                        title="Vista previa del email"
                      >
                        <Eye size={15} />
                      </button>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {leadEmail
                        ? `Se enviará a ${leadEmail}.`
                        : "Este lead no tiene email en la ficha: escríbelo abajo y se guardará."}
                    </div>
                  </div>
                </label>

                {sendByEmail && (
                  <div className="px-3 pb-3">
                    <input
                      type="email"
                      value={emailValue}
                      onChange={(e) => setEmailValue(e.target.value)}
                      disabled={status === "loading"}
                      placeholder="cliente@sudominio.es"
                      className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            {step === 1 && (
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={status === "loading"}
                  className="flex-1 px-4 py-3 rounded-2xl border border-gray-200 text-gray-600 font-semibold hover:bg-gray-50 disabled:opacity-50 transition-all"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={!selectedSector || status === "loading" || sectorsLoading}
                  className="flex-1 px-4 py-3 rounded-2xl bg-gradient-to-r from-purple-600 to-violet-600 text-white font-bold hover:from-purple-700 hover:to-violet-700 disabled:opacity-50 shadow-lg shadow-purple-200/50 transition-all active:scale-95 flex items-center justify-center gap-2"
                >
                  {status === "loading" ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Creando cuenta…
                    </>
                  ) : sendByWhatsApp ? (
                    "Crear y Enviar"
                  ) : (
                    "Crear Accesos"
                  )}
                </button>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
      <AccesosEmailPreviewModal
        open={emailPreviewOpen}
        onClose={() => setEmailPreviewOpen(false)}
        companyId={null}
        email={(emailValue || "").trim() || leadEmail || ""}
        nombre={leadName}
      />
      <WhatsAppPreviewModal
        open={waPreviewOpen}
        onClose={() => setWaPreviewOpen(false)}
        type="accesos"
        recipientName={leadName}
      />
    </>
  );
}
